import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Check, ArrowRight, Users, ShieldCheck, Loader2, Plane, Ticket, Utensils, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "../context/AppContext";
import { api, formatApiError } from "../lib/api";

const REGION_ORDER = ["Cancun", "Riviera Maya", "Archaeological Site", "Tourist Site"];

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.2em] text-white/50">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

const inputCls =
  "w-full bg-transparent border-b border-white/20 focus:border-[var(--gold)] rounded-none px-0 py-3 text-white placeholder:text-white/30 outline-none transition-colors";

export default function Booking() {
  const { t } = useApp();
  const [params] = useSearchParams();
  const [dests, setDests] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [tripType, setTripType] = useState("one_way");
  const [passengers, setPassengers] = useState(2);
  const [arrival, setArrival] = useState({ airline: "", flight_number: "", date: "", time: "" });
  const [departure, setDeparture] = useState({ airline: "", flight_number: "", date: "", time: "" });
  const [customer, setCustomer] = useState({ customer_name: "", customer_email: "", customer_phone: "", hotel_name: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [booking, setBooking] = useState(null);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    api.get("/destinations").then((r) => {
      setDests(r.data);
      const pre = params.get("dest");
      if (pre) {
        const found = r.data.find((d) => d.id === pre);
        if (found) setSelected(found);
      }
    });
  }, [params]);

  const grouped = useMemo(() => {
    const filtered = dests.filter((d) => d.name.toLowerCase().includes(search.toLowerCase()));
    const g = {};
    filtered.forEach((d) => {
      (g[d.region] = g[d.region] || []).push(d);
    });
    return g;
  }, [dests, search]);

  const isPrivate = selected?.pricing_type === "private";
  const total = selected
    ? isPrivate
      ? selected.price
      : selected.price * passengers * (tripType === "round_trip" ? 2 : 1)
    : 0;

  const submit = async () => {
    if (!selected) return toast.error(t.booking.selectDest);
    if (!customer.customer_name || !customer.customer_email || !customer.customer_phone) {
      return toast.error("Please complete your contact details.");
    }
    setSubmitting(true);
    try {
      const payload = {
        trip_type: tripType,
        destination_id: selected.id,
        passengers,
        ...customer,
        arrival,
        departure: tripType === "round_trip" ? departure : null,
      };
      const { data } = await api.post("/bookings", payload);
      setBooking({ id: data.booking_id, total: data.total_price });
      toast.success(t.booking.success);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post(`/bookings/${booking.id}/pay`);
      if (data.init_point) window.location.href = data.init_point;
    } catch (e) {
      const detail = formatApiError(e.response?.data?.detail);
      toast.error(detail);
    } finally {
      setPaying(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] pt-28 md:pt-36 pb-24">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="mb-12">
          <p className="text-xs tracking-[0.3em] uppercase text-[var(--gold)] mb-4">HRH · Reservations</p>
          <h1 className="font-serif font-light text-4xl md:text-6xl tracking-tight">{t.booking.title}</h1>
          <p className="text-white/50 mt-3">{t.booking.sub}</p>
        </div>

        {booking ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="booking-confirmation"
            className="max-w-2xl border border-[var(--gold)]/40 bg-[var(--purple)]/40 p-10 gold-glow"
          >
            <div className="w-14 h-14 grid place-items-center border border-[var(--gold)] text-[var(--gold)] mb-6">
              <Check className="w-7 h-7" />
            </div>
            <h2 className="font-serif text-3xl mb-3">{t.booking.summary}</h2>
            <p className="text-white/70 mb-6">{t.booking.success}</p>
            <div className="space-y-2 text-sm border-t border-white/10 pt-6">
              <div className="flex justify-between"><span className="text-white/50">{selected?.name}</span><span>{t.booking.privateVan}</span></div>
              <div className="flex justify-between"><span className="text-white/50">{t.booking.passengers}</span><span>{passengers}</span></div>
              <div className="flex justify-between text-[var(--gold)] text-lg pt-3 border-t border-white/10 mt-3"><span>{t.booking.total}</span><span className="font-serif">${booking.total} USD</span></div>
            </div>
            <button onClick={pay} disabled={paying} data-testid="pay-now-button" className="btn-fill mt-8 w-full border border-[var(--gold)] text-[var(--gold)] py-4 text-xs uppercase tracking-[0.2em] flex items-center justify-center gap-3 transition-colors duration-300">
              {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : t.booking.pay}
            </button>
            <Link to="/" className="block text-center mt-4 text-xs uppercase tracking-widest text-white/50 hover:text-white">← Home</Link>
          </motion.div>
        ) : (
          <div className="grid lg:grid-cols-[1fr_400px] gap-10">
            {/* LEFT: form */}
            <div className="space-y-14">
              {/* Trip type */}
              <div>
                <h3 className="text-sm uppercase tracking-[0.2em] text-white/80 mb-5">{t.booking.tripType}</h3>
                <div className="grid grid-cols-2 gap-3 max-w-md">
                  {[["one_way", t.booking.oneWay], ["round_trip", t.booking.roundTrip]].map(([v, label]) => (
                    <button
                      key={v}
                      data-testid={`trip-${v}`}
                      onClick={() => setTripType(v)}
                      className={`border py-4 text-xs uppercase tracking-[0.2em] transition-colors ${
                        tripType === v ? "border-[var(--gold)] text-[var(--gold)] bg-[var(--gold)]/5" : "border-white/15 text-white/60 hover:border-white/40"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Destination */}
              <div>
                <h3 className="text-sm uppercase tracking-[0.2em] text-white/80 mb-5">{t.booking.destination}</h3>
                <div className="relative max-w-md mb-5">
                  <Search className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <input
                    data-testid="destination-search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t.booking.searchPh}
                    className={`${inputCls} pl-7`}
                  />
                </div>
                <div className="max-h-72 overflow-y-auto border border-white/10 divide-y divide-white/5">
                  {REGION_ORDER.filter((r) => grouped[r]?.length).map((region) => (
                    <div key={region}>
                      <div className="px-4 py-2 text-[10px] uppercase tracking-[0.25em] text-[var(--gold)]/70 bg-white/[0.02] sticky top-0">{region}</div>
                      {grouped[region].map((d) => (
                        <button
                          key={d.id}
                          data-testid={`dest-option-${d.id}`}
                          onClick={() => setSelected(d)}
                          className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors ${
                            selected?.id === d.id ? "bg-[var(--gold)]/10" : "hover:bg-white/[0.03]"
                          }`}
                        >
                          <span className="flex items-center gap-3">
                            {selected?.id === d.id && <Check className="w-4 h-4 text-[var(--gold)]" />}
                            <span className={selected?.id === d.id ? "text-white" : "text-white/70"}>{d.name}</span>
                          </span>
                          <span className="text-[var(--gold)] text-sm whitespace-nowrap">
                            ${d.price}
                            <span className="text-white/40 text-[10px] ml-1">{d.pricing_type === "private" ? t.destinations.privateTour : t.destinations.perPerson}</span>
                          </span>
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              {/* Passengers */}
              <div>
                <h3 className="text-sm uppercase tracking-[0.2em] text-white/80 mb-5 flex items-center gap-2"><Users className="w-4 h-4" /> {t.booking.passengers}</h3>
                <div className="flex flex-wrap gap-2 max-w-lg">
                  {Array.from({ length: 14 }, (_, i) => i + 1).map((n) => (
                    <button
                      key={n}
                      data-testid={`pax-${n}`}
                      onClick={() => setPassengers(n)}
                      className={`w-12 h-12 border text-sm transition-colors ${
                        passengers === n ? "border-[var(--gold)] text-[var(--gold)] bg-[var(--gold)]/5" : "border-white/15 text-white/60 hover:border-white/40"
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>

              {/* Arrival flight */}
              <div>
                <h3 className="text-sm uppercase tracking-[0.2em] text-white/80 mb-5 flex items-center gap-2"><Plane className="w-4 h-4" /> {t.booking.arrival}</h3>
                <div className="grid sm:grid-cols-2 gap-6 max-w-2xl">
                  <Field label={t.booking.airline}><input data-testid="arrival-airline" className={inputCls} value={arrival.airline} onChange={(e) => setArrival({ ...arrival, airline: e.target.value })} /></Field>
                  <Field label={t.booking.flightNo}><input data-testid="arrival-flight" className={inputCls} value={arrival.flight_number} onChange={(e) => setArrival({ ...arrival, flight_number: e.target.value })} /></Field>
                  <Field label={t.booking.date}><input type="date" data-testid="arrival-date" className={`${inputCls} [color-scheme:dark]`} value={arrival.date} onChange={(e) => setArrival({ ...arrival, date: e.target.value })} /></Field>
                  <Field label={t.booking.time}><input type="time" data-testid="arrival-time" className={`${inputCls} [color-scheme:dark]`} value={arrival.time} onChange={(e) => setArrival({ ...arrival, time: e.target.value })} /></Field>
                </div>
              </div>

              {/* Departure flight (round trip) */}
              {tripType === "round_trip" && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}>
                  <h3 className="text-sm uppercase tracking-[0.2em] text-white/80 mb-5 flex items-center gap-2"><Plane className="w-4 h-4 rotate-180" /> {t.booking.departure}</h3>
                  <div className="grid sm:grid-cols-2 gap-6 max-w-2xl">
                    <Field label={t.booking.airline}><input data-testid="departure-airline" className={inputCls} value={departure.airline} onChange={(e) => setDeparture({ ...departure, airline: e.target.value })} /></Field>
                    <Field label={t.booking.flightNo}><input data-testid="departure-flight" className={inputCls} value={departure.flight_number} onChange={(e) => setDeparture({ ...departure, flight_number: e.target.value })} /></Field>
                    <Field label={t.booking.date}><input type="date" data-testid="departure-date" className={`${inputCls} [color-scheme:dark]`} value={departure.date} onChange={(e) => setDeparture({ ...departure, date: e.target.value })} /></Field>
                    <Field label={t.booking.time}><input type="time" data-testid="departure-time" className={`${inputCls} [color-scheme:dark]`} value={departure.time} onChange={(e) => setDeparture({ ...departure, time: e.target.value })} /></Field>
                  </div>
                </motion.div>
              )}

              {/* Customer */}
              <div>
                <h3 className="text-sm uppercase tracking-[0.2em] text-white/80 mb-5">{t.booking.yourDetails}</h3>
                <div className="grid sm:grid-cols-2 gap-6 max-w-2xl">
                  <Field label={t.booking.name}><input data-testid="cust-name" className={inputCls} value={customer.customer_name} onChange={(e) => setCustomer({ ...customer, customer_name: e.target.value })} /></Field>
                  <Field label={t.booking.email}><input type="email" data-testid="cust-email" className={inputCls} value={customer.customer_email} onChange={(e) => setCustomer({ ...customer, customer_email: e.target.value })} /></Field>
                  <Field label={t.booking.phone}><input data-testid="cust-phone" className={inputCls} value={customer.customer_phone} onChange={(e) => setCustomer({ ...customer, customer_phone: e.target.value })} /></Field>
                  <Field label={t.booking.hotel}><input data-testid="cust-hotel" className={inputCls} value={customer.hotel_name} onChange={(e) => setCustomer({ ...customer, hotel_name: e.target.value })} /></Field>
                  <div className="sm:col-span-2">
                    <Field label={t.booking.notes}><textarea rows={2} data-testid="cust-notes" className={inputCls} value={customer.notes} onChange={(e) => setCustomer({ ...customer, notes: e.target.value })} /></Field>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT: summary */}
            <div className="lg:sticky lg:top-28 h-fit border border-[var(--border)] bg-[var(--purple)]/30 p-8">
              <h3 className="font-serif text-2xl mb-6">{t.booking.summary}</h3>
              <div className="space-y-4 text-sm">
                <div className="flex justify-between border-b border-white/10 pb-3">
                  <span className="text-white/50">{t.booking.destination}</span>
                  <span className="text-right max-w-[60%]">{selected ? selected.name : "—"}</span>
                </div>
                <div className="flex justify-between border-b border-white/10 pb-3">
                  <span className="text-white/50">{t.booking.tripType}</span>
                  <span>{tripType === "round_trip" ? t.booking.roundTrip : t.booking.oneWay}</span>
                </div>
                <div className="flex justify-between border-b border-white/10 pb-3">
                  <span className="text-white/50">{t.booking.vehicle}</span>
                  <span>{t.booking.privateVan}</span>
                </div>
                <div className="flex justify-between border-b border-white/10 pb-3">
                  <span className="text-white/50">{t.booking.passengers}</span>
                  <span>{passengers}</span>
                </div>
                <div className="flex justify-between border-b border-white/10 pb-3 text-white/70">
                  <span className="text-white/50">{isPrivate ? t.booking.privateTour : t.booking.subtotal}</span>
                  <span data-testid="booking-unit-detail">
                    {selected
                      ? isPrivate
                        ? `$${selected.price} USD`
                        : `$${selected.price} × ${passengers}${tripType === "round_trip" ? " × 2" : ""}`
                      : "—"}
                  </span>
                </div>
              </div>

              <div className="flex items-start gap-3 mt-6 p-4 bg-black/30 border border-[var(--gold)]/20">
                <ShieldCheck className="w-5 h-5 text-[var(--gold)] shrink-0 mt-0.5" />
                <p className="text-xs text-white/60 leading-relaxed">{t.booking.meet}</p>
              </div>

              {isPrivate && selected?.includes?.length > 0 && (
                <div className="mt-4 p-4 bg-[var(--gold)]/[0.06] border border-[var(--gold)]/20">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[var(--gold)] mb-3">{t.inc.title}</p>
                  <ul className="space-y-2">
                    {selected.includes.map((item) => {
                      const map = { guide: [UserCheck, t.inc.guide], tickets: [Ticket, t.inc.tickets], meal: [Utensils, t.inc.meal] };
                      const [Icon, label] = map[item] || [Check, item];
                      return (
                        <li key={item} className="flex items-center gap-2 text-sm text-white/75">
                          <Icon className="w-4 h-4 text-[var(--gold)]" /> {label}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              <div className="flex items-end justify-between mt-8 pt-6 border-t border-white/15">
                <span className="text-xs uppercase tracking-widest text-white/50">{t.booking.total}</span>
                <span data-testid="booking-total" className="font-serif text-4xl text-[var(--gold)]">${total} <span className="text-sm text-white/40">USD</span></span>
              </div>

              <button
                onClick={submit}
                disabled={submitting}
                data-testid="confirm-reservation-button"
                className="btn-fill mt-8 w-full border border-[var(--gold)] text-[var(--gold)] py-4 text-xs uppercase tracking-[0.2em] flex items-center justify-center gap-3 transition-colors duration-300"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{t.booking.submit} <ArrowRight className="w-4 h-4" /></>}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
